import { OTTERBRICK_KAKAO_JS_KEY } from '@config/environment';

export interface ILocation {
  latitude: number;
  longitude: number;
  name: string;
  address: string;
}
interface ILatLng {
  getLat(): number;
  getLng(): number;
}
export interface IKakaoMap {
  setCenter(position: ILatLng): void;
  setLevel(level: number): void;
  addControl(control: object, position: number): void;
  relayout(): void;
  setMapTypeId(type: number): void;
}
export interface IKakaoMarker {
  setPosition(position: ILatLng): void;
  getPosition(): ILatLng;
  setMap(map: IKakaoMap | null): void;
}
interface ISearchResult {
  id?: string;
  place_name?: string;
  address_name: string;
  road_address_name?: string;
  x: string;
  y: string;
}
type TSearchCallback = (data: ISearchResult[], status: string) => void;
export interface IKakaoMaps {
  load(callback: () => void): void;
  LatLng: new (latitude: number, longitude: number) => ILatLng;
  Map: new (
    element: HTMLElement,
    options: {
      center: ILatLng;
      level: number;
      draggable?: boolean;
      scrollwheel?: boolean;
      disableDoubleClickZoom?: boolean;
      keyboardShortcuts?: boolean;
    },
  ) => IKakaoMap;
  Marker: new (options: { position: ILatLng; map?: IKakaoMap; draggable: boolean }) => IKakaoMarker;
  MapTypeId: { ROADMAP: number; HYBRID: number };
  ZoomControl: new () => object;
  ControlPosition: { RIGHT: number };
  event: {
    addListener(target: IKakaoMap | IKakaoMarker, name: string, callback: (event: { latLng: ILatLng }) => void): void;
    removeListener(
      target: IKakaoMap | IKakaoMarker,
      name: string,
      callback: (event: { latLng: ILatLng }) => void,
    ): void;
  };
  services: {
    Places: new () => { keywordSearch(query: string, callback: TSearchCallback): void };
    Geocoder: new () => {
      addressSearch(query: string, callback: TSearchCallback): void;
      coord2Address(
        longitude: number,
        latitude: number,
        callback: (
          data: { address?: { address_name: string }; road_address?: { address_name: string } }[],
          status: string,
        ) => void,
      ): void;
    };
    Status: { OK: string; ZERO_RESULT: string };
  };
}

// 이 모듈을 사용하는 모든 지도는 같은 Promise를 공유한다.
// 첫 호출에서 SDK 로드를 시작하고, 로딩 중·완료 후의 호출은 같은 Promise를 반환해
// 카드마다 인증 스크립트를 중복 요청하지 않는다. 지도 객체는 각 화면에서 별도로 생성한다.
// 로드 실패 시에만 null로 초기화해 재시도를 허용하며, 새로고침하면 모듈 메모리도 초기화된다.
// 공식 SDK 가이드: https://apis.map.kakao.com/web/guide/
// 공식 운영진의 쿼터 집계 설명 (스크립트 로드 기준, 지도 객체 추가 생성은 별도 집계 없음):
// https://devtalk.kakao.com/t/topic/144835
let sdkPromise: Promise<IKakaoMaps> | null = null;
const getMaps = () => (window as Window & { kakao?: { maps?: IKakaoMaps } }).kakao?.maps;

export const loadKakaoMaps = (): Promise<IKakaoMaps> => {
  if (sdkPromise) return sdkPromise;
  const appKey = OTTERBRICK_KAKAO_JS_KEY;
  if (!appKey) return Promise.reject(new Error('지도 키가 반영되지 않았습니다. 개발 서버를 재시작해주세요.'));
  sdkPromise = new Promise<IKakaoMaps>((resolve, reject) => {
    const ready = () => {
      const maps = getMaps();
      if (!maps) {
        reject(new Error('지도 SDK를 불러오지 못했습니다. 등록된 도메인과 네트워크를 확인해주세요.'));
        return;
      }
      maps.load(() => resolve(maps));
    };
    if (getMaps()?.services) {
      ready();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${encodeURIComponent(appKey)}&autoload=false&libraries=services`;
    script.async = true;
    const timeout = window.setTimeout(() => {
      script.remove();
      reject(new Error('지도 로드 시간 초과'));
    }, 15000);
    script.onload = () => {
      window.clearTimeout(timeout);
      ready();
    };
    script.onerror = () => {
      window.clearTimeout(timeout);
      script.remove();
      reject(new Error('지도 SDK를 불러오지 못했습니다. 등록된 도메인과 네트워크를 확인해주세요.'));
    };
    if (process.env.NODE_ENV === 'development') {
      console.info('[KakaoMaps] SDK 스크립트 요청');
    }
    document.head.appendChild(script);
  }).catch((error) => {
    sdkPromise = null;
    throw error;
  });
  return sdkPromise;
};
